<script setup lang="ts">
import { TSuspense } from '@teachino/shared-design-system';
import { readFragment } from '@teachino/shared-infrastructure';
import { $pluralize } from '@teachino/shared-i18n';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { useQuery } from '@urql/vue';
import { computed, onMounted } from 'vue';
import { baseLicenseFragment } from './base-license.fragment';
import DpaOnlyLicense from './DpaOnlyLicense.vue';
import { isDpaOnlyLicense } from './dpa-only-license';
import { licensesQuery } from './licenses.query';
import PersonalLicense from './PersonalLicense.vue';
import TenantLicense from './TenantLicense.vue';
import UpgradeInformation from './UpgradeInformation.vue';
import UpgradeSchoolLicenseSection from './UpgradeSchoolLicenseSection.vue';

const di = useGlobalDependencyProvider();
const analytics = di.inject('analytics');

onMounted(() => {
  analytics.track('upgrade-open-page');
});

const licensesQueryResult = useQuery({ query: licensesQuery });

const licenses = computed(
  () =>
    licensesQueryResult.data.value?.licenses.map((license) => {
      return {
        fragment: license,
        base: readFragment(baseLicenseFragment, license),
      };
    }) ?? [],
);

const dpaOnlyLicenses = computed(() =>
  licenses.value.filter((license) => isDpaOnlyLicense(license.base)),
);

const regularLicenses = computed(() =>
  licenses.value.filter((license) => !isDpaOnlyLicense(license.base)),
);

const hasPaidPlan = computed<boolean>(() =>
  regularLicenses.value.some((license) => license.base.plan.type === 'PAID'),
);

const personalLicenses = computed(() =>
  regularLicenses.value.filter(
    (license) =>
      license.base.holderType === 'USER' &&
      (hasPaidPlan.value ? license.base.plan.type === 'PAID' : true),
  ),
);

const tenantLicenses = computed(() =>
  regularLicenses.value.filter(
    (license) => license.base.holderType === 'TENANT',
  ),
);

const licenseCount = computed(
  () => personalLicenses.value.length + tenantLicenses.value.length,
);

const showUpgradeOffers = computed(() => !hasPaidPlan.value);
const showSchoolOffer = computed(() => tenantLicenses.value.length === 0);
</script>

<template>
  <TSuspense :response="licensesQueryResult">
    <template #default>
      <div class="w-full bg-slate-50 px-4 py-4 md:px-8 md:py-6">
        <div class="mx-auto flex w-full max-w-4xl flex-col gap-6">
          <UpgradeInformation v-if="showUpgradeOffers" />

          <UpgradeSchoolLicenseSection v-if="showSchoolOffer" />

          <div v-if="dpaOnlyLicenses.length > 0" class="flex flex-col gap-4">
            <DpaOnlyLicense
              v-for="({ fragment }, index) in dpaOnlyLicenses"
              :key="index"
              :license="fragment"
            />
          </div>

          <section
            v-if="licenseCount > 0"
            class="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm"
          >
            <div class="border-b border-slate-100 px-5 py-4">
              <h2 class="text-lg font-semibold text-slate-900">
                {{
                  $pluralize(
                    'upgrade.license_page.title_0',
                    'upgrade.license_page.title_1',
                    'upgrade.license_page.title_many',
                    licenseCount,
                  )
                }}
              </h2>
            </div>

            <div class="flex flex-col gap-4 p-4 md:p-5">
              <TenantLicense
                v-for="({ fragment }, index) in tenantLicenses"
                :key="index"
                :license="fragment"
              />

              <PersonalLicense
                v-for="({ fragment }, index) in personalLicenses"
                :key="index"
                :license="fragment"
              />
            </div>
          </section>
        </div>
      </div>
    </template>
  </TSuspense>
</template>
